import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import {
  Heart, Shield, Building2, Users, Wallet, TrendingUp,
  ArrowRight, ArrowLeft, ChevronRight, Zap, Globe,
  DollarSign, BarChart3, Smartphone, Award, Star,
  HeartHandshake, Activity, Clock, Target, Layers,
  Briefcase, AlertTriangle, Lightbulb, Network, MapPin,
  Pill, Stethoscope, Factory, CircleDollarSign, Rocket,
  Play, Pause, X, Send, CheckCircle2, MessageCircle, Home
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { insertDemoRequestSchema } from "@shared/schema";
import healthWalletLogo from "@assets/generated_images/healthwallet_shield_heart_logo.png";


const TOTAL_SLIDES = 16;
const SLIDESHOW_INTERVAL = 8000;

const SLIDE_NAMES = [
  "Title", "Problem", "Insight", "Solution", "Blue Ocean",
  "How It Works", "Atomic Network", "Community Wallet", "Why Minnesota",
  "Traction", "Market", "Team", "The Raise", "Vision", "Investor FAQ", "Closing",
];

function SlideWrapper({ children, className = "", slideIndex }: { children: React.ReactNode; className?: string; slideIndex?: number }) {
  return (
    <div className={`flex flex-col items-center justify-center min-h-screen w-full px-4 py-8 sm:px-6 sm:py-12 md:px-16 lg:px-24 ${className}`} data-testid={slideIndex !== undefined ? `slide-${slideIndex + 1}` : undefined}>
      <div className="w-full max-w-5xl">
        {children}
      </div>
    </div>
  );
}

function SlideNumber({ current, total, onSlideSelect, slideNames }: {
  current: number;
  total: number;
  onSlideSelect: (index: number) => void;
  slideNames: string[];
}) {
  return (
    <div className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-50 pb-[env(safe-area-inset-bottom)]">
      <div className="flex gap-1.5 sm:gap-1.5">
        {Array.from({ length: total }, (_, i) => (
          <button
            key={i}
            onClick={() => onSlideSelect(i)}
            title={`${i + 1}. ${slideNames[i]}`}
            aria-label={`Go to slide ${i + 1}: ${slideNames[i]}`}
            data-testid={`dot-slide-${i + 1}`}
            className="p-2 -m-2 flex items-center justify-center cursor-pointer"
          >
            <span
              className={`block rounded-full transition-all duration-300 ${
                i === current ? "w-6 h-2 sm:h-1.5 bg-white" : "w-2 sm:w-1.5 h-2 sm:h-1.5 bg-white/40 hover:bg-white/70"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

function Slide1Title() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(190,70%,25%)] via-[hsl(190,70%,32%)] to-[hsl(215,50%,20%)] text-white">
      <div className="flex flex-col items-center text-center gap-8">
        <div className="flex flex-col items-center gap-4">
          <img 
            src={healthWalletLogo} 
            alt="HealthWallet Logo" 
            className="w-24 h-24 md:w-32 md:h-32 object-contain drop-shadow-[0_0_30px_rgba(255,255,255,0.15)]"
            width="1024"
            height="1024"
            data-testid="img-pitch-logo"
          />
          <span className="text-3xl font-bold tracking-wider text-white">HealthWallet</span>
        </div>
        <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest">Infrastructure for the Non-Insured Workforce</p>
        <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight">
          Financial Infrastructure<br />for <span className="text-[hsl(14,70%,60%)]">30M+ Workers</span><br />Without Access
        </h1>
        <p className="text-base sm:text-lg md:text-xl text-white/70 max-w-2xl">
          Traditional models don't serve them. Charity doesn't scale. We're building the payment infrastructure layer for the non-insured workforce.
        </p>
        <div className="flex items-center gap-2 text-white/50 text-sm mt-8">
          <span>Seed Round</span>
          <span className="mx-2">|</span>
          <span>{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
        </div>
      </div>
    </SlideWrapper>
  );
}

function Slide2Problem() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(215,50%,12%)] to-[hsl(215,50%,18%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">The Problem</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 leading-tight">
        The system fails <span className="text-[hsl(14,70%,60%)]">all three</span> simultaneously
      </h2>
      <p className="text-white/50 mb-6 sm:mb-10 text-base sm:text-lg">Three stakeholders. Three breakdowns. One interconnected crisis.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white/5 border border-white/10 rounded-md p-4 sm:p-6">
          <Briefcase className="w-7 h-7 sm:w-8 sm:h-8 text-[hsl(14,70%,60%)] mb-3 sm:mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold mb-3">Small Employers</h3>
          <ul className="space-y-2 text-white/70 text-sm">
            <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Cannot afford $500-$1,500 PEPM traditional plans</li>
            <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Face volatile annual renewals</li>
            <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Offer nothing instead</li>
          </ul>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-md p-4 sm:p-6">
          <Users className="w-7 h-7 sm:w-8 sm:h-8 text-[hsl(14,70%,60%)] mb-3 sm:mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold mb-3">Workers</h3>
          <ul className="space-y-2 text-white/70 text-sm">
            <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Live paycheck to paycheck</li>
            <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Delay care until emergencies</li>
            <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Accumulate medical debt</li>
          </ul>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-md p-4 sm:p-6">
          <Building2 className="w-7 h-7 sm:w-8 sm:h-8 text-[hsl(14,70%,60%)] mb-3 sm:mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold mb-3">Providers</h3>
          <ul className="space-y-2 text-white/70 text-sm">
            <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Carry bad debt</li>
            <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Wait 30-60 days for payment</li>
            <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Spend 20%+ of revenue on billing</li>
          </ul>
        </div>
      </div>
    </SlideWrapper>
  );
}

function Slide3Insight() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(190,70%,25%)] to-[hsl(190,60%,18%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">The Insight</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
        The traditional model pools risk.<br />The real bottleneck is <span className="text-[hsl(14,70%,60%)]">liquidity</span>.
      </h2>
      <p className="text-white/60 text-base sm:text-lg mb-8 sm:mb-12 max-w-3xl">
        The system tries to pool risk before solving the fundamental problem: immediate, pre-funded payment for routine and chronic care.
      </p>
      <div className="bg-white/5 border border-white/10 rounded-md p-5 sm:p-8 mb-6 sm:mb-8">
        <h3 className="font-semibold text-lg sm:text-xl mb-4 sm:mb-6 text-center">If we can...</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-[hsl(190,70%,30%)] border border-[hsl(190,70%,40%)] flex items-center justify-center mx-auto mb-3">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <p className="text-white/80 text-sm">Aggregate SME contributions</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-[hsl(190,70%,30%)] border border-[hsl(190,70%,40%)] flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <p className="text-white/80 text-sm">Negotiate purchasing power</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-[hsl(190,70%,30%)] border border-[hsl(190,70%,40%)] flex items-center justify-center mx-auto mb-3">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <p className="text-white/80 text-sm">Pay providers instantly</p>
          </div>
        </div>
      </div>
      <div className="bg-[hsl(14,70%,40%)]/20 border border-[hsl(14,70%,50%)]/30 rounded-md p-6 text-center">
        <p className="text-xl md:text-2xl font-semibold">
          Fix liquidity first <ArrowRight className="w-5 h-5 inline mx-2" /> <span className="text-[hsl(14,70%,60%)]">compress costs structurally</span>
        </p>
      </div>
    </SlideWrapper>
  );
}

function Slide4Solution() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(215,50%,12%)] to-[hsl(215,50%,18%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">The Solution</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 leading-tight">
        The Workforce <span className="text-[hsl(14,70%,60%)]">Health Wallet</span>
      </h2>
      <p className="text-white/50 mb-6 sm:mb-10 text-base sm:text-lg">Not a traditional plan. Not reimbursement. <strong className="text-white">Infrastructure.</strong></p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 mb-6 sm:mb-8">
        {[
          { icon: Wallet, title: "Employer-Funded Digital Wallet", desc: "Pre-funded accounts with fixed, predictable employer contributions" },
          { icon: Zap, title: "Direct-to-Provider Payment Rail", desc: "Instant payment to providers, eliminating billing overhead and bad debt" },
          { icon: Heart, title: "Condition-Based Purchasing Cooperatives", desc: "Volume-based negotiation for chronic conditions (NCD) to compress costs" },
          { icon: Smartphone, title: "Portable Digital Health Record", desc: "Workers own their health data and wallet across employers" },
          { icon: CircleDollarSign, title: "Payroll-Integrated Funding", desc: "Seamless contribution flows directly from employer payroll systems" },
        ].map((feature) => (
          <div key={feature.title} className="flex gap-4 bg-white/5 border border-white/10 rounded-md p-5">
            <feature.icon className="w-6 h-6 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold mb-1">{feature.title}</h3>
              <p className="text-white/60 text-sm">{feature.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white/5 border border-white/10 rounded-md p-5">
        <p className="text-white/60 text-sm text-center">
          <Shield className="w-4 h-4 inline mr-2" />
          <strong className="text-white">A membership-based health infrastructure</strong> — a technology platform facilitating pre-funded healthcare access, not a regulated product
        </p>
      </div>
    </SlideWrapper>
  );
}

function Slide5BlueOcean() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(190,70%,25%)] via-[hsl(190,70%,32%)] to-[hsl(215,50%,20%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">The Blue Ocean</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 leading-tight">
        We don't compete with traditional plans.<br />We compete with <span className="text-[hsl(14,70%,60%)]">zero access</span>.
      </h2>
      <p className="text-white/60 text-base sm:text-lg mb-8 sm:mb-12 max-w-3xl">
        Our target market is massive, fragmented, and completely excluded from traditional healthcare access.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
        <div className="bg-white/5 border border-white/10 rounded-md p-5 sm:p-8">
          <h3 className="font-semibold text-lg mb-4 sm:mb-6 text-center text-white/80">Traditional Model</h3>
          <div className="space-y-3">
            {["$500-$1,500 PEPM cost", "Complex eligibility screening", "Annual volatile renewals", "Designed for large employers", "Excludes 30M+ workers"].map((item) => (
              <div key={item} className="flex items-center gap-3 text-white/50 text-sm">
                <div className="w-5 h-5 rounded-full border border-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-white/30 text-xs">x</span>
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[hsl(190,70%,30%)]/30 border border-[hsl(190,70%,40%)]/40 rounded-md p-5 sm:p-8">
          <h3 className="font-semibold text-lg mb-4 sm:mb-6 text-center text-[hsl(14,70%,60%)]">HealthWallet</h3>
          <div className="space-y-3">
            {["$100-$200 PEPM contribution", "No eligibility screening", "No renewals, fixed cost", "Built for the SME workforce", "Serves the excluded workforce directly"].map((item) => (
              <div key={item} className="flex items-center gap-3 text-white/80 text-sm">
                <div className="w-5 h-5 rounded-full bg-[hsl(14,70%,50%)] flex items-center justify-center flex-shrink-0">
                  <ChevronRight className="w-3 h-3 text-white" />
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
}

function Slide6HowItWorks() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(215,50%,12%)] to-[hsl(215,50%,18%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">How It Works</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-8 sm:mb-12 leading-tight">Three stakeholders. <span className="text-[hsl(14,70%,60%)]">All win.</span></h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-[hsl(190,70%,30%)] rounded-md p-4 sm:p-6 h-full">
          <Briefcase className="w-8 h-8 text-white/80 mb-4" />
          <div className="text-[hsl(14,70%,60%)] text-sm font-bold uppercase tracking-wider mb-3">Employer</div>
          <div className="space-y-3">
            {[
              { label: "$100-$200", sub: "per employee / month" },
              { label: "Fixed", sub: "predictable cost" },
              { label: "Zero", sub: "eligibility screening" },
              { label: "No", sub: "renewals" },
            ].map((item) => (
              <div key={item.sub} className="flex items-baseline gap-2">
                <span className="text-white font-bold text-lg">{item.label}</span>
                <span className="text-white/60 text-xs">{item.sub}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[hsl(14,70%,40%)] rounded-md p-4 sm:p-6 h-full">
          <Users className="w-8 h-8 text-white/80 mb-4" />
          <div className="text-white/90 text-sm font-bold uppercase tracking-wider mb-3">Worker</div>
          <div className="space-y-3 text-white/80 text-sm">
            <div className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />Uses pre-funded wallet</div>
            <div className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />Direct payment to provider</div>
            <div className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />Negotiated pricing</div>
            <div className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />0% payment plans</div>
          </div>
        </div>
        <div className="bg-[hsl(190,60%,25%)] rounded-md p-4 sm:p-6 h-full">
          <Building2 className="w-8 h-8 text-white/80 mb-4" />
          <div className="text-[hsl(14,70%,60%)] text-sm font-bold uppercase tracking-wider mb-3">Provider</div>
          <div className="space-y-3 text-white/80 text-sm">
            <div className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Paid instantly</div>
            <div className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Lower bad debt</div>
            <div className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Reduced billing overhead</div>
            <div className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />Incentives aligned</div>
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
}

function Slide7AtomicNetwork() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(190,70%,25%)] via-[hsl(190,70%,32%)] to-[hsl(215,50%,20%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">The Atomic Network</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 leading-tight">
        Small unit. <span className="text-[hsl(14,70%,60%)]">Compounding</span> network.
      </h2>
      <p className="text-white/60 text-base sm:text-lg mb-6 sm:mb-10 max-w-3xl">
        Every atomic unit we deploy creates a self-reinforcing growth loop that compounds purchasing power.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-10">
        {[
          { icon: Briefcase, value: "1", label: "SME" },
          { icon: Users, value: "8", label: "Employees" },
          { icon: Stethoscope, value: "1", label: "Primary / Urgent Care" },
          { icon: Pill, value: "1", label: "Pharmacy" },
        ].map((unit) => (
          <div key={unit.label} className="bg-[hsl(190,70%,30%)] border border-[hsl(190,70%,40%)] rounded-md p-5 text-center">
            <unit.icon className="w-6 h-6 text-[hsl(14,70%,60%)] mx-auto mb-2" />
            <p className="text-3xl font-bold">{unit.value}</p>
            <p className="text-white/60 text-xs mt-1">{unit.label}</p>
          </div>
        ))}
      </div>
      <div className="bg-white/5 border border-white/10 rounded-md p-4 sm:p-6">
        <h3 className="font-semibold mb-3 sm:mb-4 text-center text-sm sm:text-base">As Density Increases</h3>
        <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-0 text-xs sm:text-sm text-white/70">
          <span className="bg-white/10 rounded-md px-3 py-2">Pharmacy prices compress</span>
          <ArrowRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 hidden md:block" />
          <ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 md:hidden rotate-90" />
          <span className="bg-white/10 rounded-md px-3 py-2">Chronic care bundles negotiate down</span>
          <ArrowRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 hidden md:block" />
          <ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 md:hidden rotate-90" />
          <span className="bg-white/10 rounded-md px-3 py-2">Providers gain predictable cash flow</span>
          <ArrowRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 hidden md:block" />
          <ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 md:hidden rotate-90" />
          <span className="bg-white/10 rounded-md px-3 py-2">More SMEs join</span>
        </div>
        <p className="text-center mt-4 text-[hsl(14,70%,60%)] font-semibold text-sm">Purchasing power <ArrowRight className="w-3 h-3 inline mx-1" /> better pricing <ArrowRight className="w-3 h-3 inline mx-1" /> adoption <ArrowRight className="w-3 h-3 inline mx-1" /> network growth</p>
      </div>
    </SlideWrapper>
  );
}

function Slide8CommunityWallet() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(215,50%,12%)] to-[hsl(215,50%,18%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">Community Wallet Strategy</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 leading-tight">
        <span className="text-[hsl(14,70%,60%)]">Costco</span> for Chronic Care
      </h2>
      <p className="text-white/60 text-base sm:text-lg mb-6 sm:mb-10 max-w-3xl">
        Start with high-frequency non-communicable diseases. Aggregate volume commitments — not risk — to negotiate dramatically lower pricing.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-10">
        {[
          { icon: Activity, condition: "Diabetes", items: ["Insulin pricing", "A1C monitoring", "Continuous glucose monitors"] },
          { icon: Heart, condition: "Hypertension", items: ["Blood pressure medications", "Regular monitoring", "Lifestyle programs"] },
          { icon: Stethoscope, condition: "Musculoskeletal", items: ["Physical therapy packages", "Imaging bundles", "Pain management"] },
        ].map((card) => (
          <div key={card.condition} className="bg-white/5 border border-white/10 rounded-md p-4 sm:p-6">
            <card.icon className="w-7 h-7 sm:w-8 sm:h-8 text-[hsl(14,70%,60%)] mb-3 sm:mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">{card.condition}</h3>
            <ul className="space-y-2">
              {card.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-white/70 text-sm">
                  <ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />{item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="bg-white/5 border border-white/10 rounded-md p-4 sm:p-6 text-center">
        <p className="text-white/70 text-sm">
          Aggregate volume commitments to negotiate: <span className="text-white font-semibold">lab & imaging bundles</span>, <span className="text-white font-semibold">medication pricing</span>, and <span className="text-white font-semibold">therapy packages</span>
        </p>
      </div>
      <p className="text-white/30 text-[10px] sm:text-xs mt-4 text-center">
        HealthWallet facilitates access to healthcare services through pre-funded wallets and volume-based purchasing. HealthWallet is not an insurance company and does not provide, administer, or guarantee any health plan or policy.
      </p>
    </SlideWrapper>
  );
}

function Slide9WhyMinnesota() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(190,70%,25%)] to-[hsl(190,60%,18%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">Why Minnesota</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 leading-tight">
        Minnesota = <span className="text-[hsl(14,70%,60%)]">Medical Alley</span>
      </h2>
      <p className="text-white/60 text-base sm:text-lg mb-6 sm:mb-10 max-w-3xl">
        The ideal pilot market with unmatched healthcare infrastructure, innovation density, and a strong small business ecosystem.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[hsl(14,70%,60%)]" />
            Home To
          </h3>
          <div className="space-y-4">
            {[
              { name: "UnitedHealth Group", desc: "Largest healthcare company globally" },
              { name: "Mayo Clinic", desc: "World-renowned medical center" },
              { name: "Medtronic", desc: "Global medical technology leader" },
            ].map((org) => (
              <div key={org.name} className="bg-white/5 border border-white/10 rounded-md p-4">
                <p className="font-semibold text-white">{org.name}</p>
                <p className="text-white/50 text-sm">{org.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-[hsl(14,70%,60%)]" />
            Strategic Advantages
          </h3>
          <div className="space-y-3">
            {[
              "High healthcare innovation density",
              "Sophisticated provider ecosystem",
              "Strong small business base",
              "Established community health networks",
              "150+ healthcare providers already mapped",
            ].map((adv) => (
              <div key={adv} className="flex items-start gap-3 text-white/70">
                <ChevronRight className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />
                <span className="text-sm">{adv}</span>
              </div>
            ))}
          </div>
          <div className="bg-[hsl(14,70%,40%)]/20 border border-[hsl(14,70%,50%)]/30 rounded-md p-4 mt-6 text-center">
            <p className="text-[hsl(14,70%,60%)] font-semibold text-sm">Ideal pilot market</p>
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
}

function Slide10Traction() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(215,50%,12%)] to-[hsl(215,50%,18%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">Traction</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 leading-tight">Post-build. <span className="text-[hsl(14,70%,60%)]">Pre-scale.</span></h2>
      <p className="text-white/50 mb-6 sm:mb-10 text-base sm:text-lg">The infrastructure is built. The model is validated. We're ready to deploy.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {[
          { icon: Wallet, title: "Wallet Platform Live", desc: "Production-ready digital wallet system with employer funding flows", done: true },
          { icon: Zap, title: "Direct Payment Rail Operational", desc: "Instant provider payment infrastructure through Stripe", done: true },
          { icon: TrendingUp, title: "Provider Negotiation Framework", desc: "Volume-based pricing negotiation model built and tested", done: true },
          { icon: Briefcase, title: "Initial Employer Cohort Ready", desc: "SME partners identified and onboarding process designed", done: true },
          { icon: Heart, title: "Condition-Wallet Model Structured", desc: "NCD-based purchasing cooperative framework ready for deployment", done: true },
        ].map((item) => (
          <div key={item.title} className="flex gap-3 sm:gap-4 bg-white/5 border border-white/10 rounded-md p-4 sm:p-5">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[hsl(190,70%,30%)] border border-[hsl(190,70%,40%)] flex items-center justify-center flex-shrink-0">
              <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold mb-1 text-sm sm:text-base">{item.title}</h3>
              <p className="text-white/60 text-xs sm:text-sm">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </SlideWrapper>
  );
}

function Slide11Market() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(190,70%,25%)] via-[hsl(190,70%,32%)] to-[hsl(215,50%,20%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">Market Opportunity</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-6 sm:mb-10 leading-tight">
        A <span className="text-[hsl(14,70%,60%)]">$1.4B</span> revenue opportunity
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 mb-6 sm:mb-10">
        <div>
          <h3 className="text-lg font-semibold mb-4">Market Sizing</h3>
          <div className="space-y-4">
            {[
              { value: "~25M", label: "SME workforce (companies <50 employees)" },
              { value: "10-12M", label: "Excluded subset within SME workforce" },
              { value: "$150", label: "Average PEPM contribution" },
              { value: "~$18B", label: "National contribution pool" },
              { value: "8%", label: "Platform take rate" },
              { value: "~$1.4B", label: "Revenue opportunity" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-md p-3">
                <span className="text-lg md:text-xl font-bold text-[hsl(14,70%,60%)] flex-shrink-0 w-20 text-right">{item.value}</span>
                <span className="text-white/70 text-sm">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-4">Unit Economics</h3>
          <div className="space-y-4 mb-8">
            <div className="bg-[hsl(190,70%,30%)]/30 border border-[hsl(190,70%,40%)]/40 rounded-md p-5">
              <p className="text-white/60 text-xs uppercase tracking-wider mb-1">CAC / LTV Ratio</p>
              <p className="text-3xl font-bold text-[hsl(14,70%,60%)]">7-10x</p>
            </div>
            <div className="bg-[hsl(190,70%,30%)]/30 border border-[hsl(190,70%,40%)]/40 rounded-md p-5">
              <p className="text-white/60 text-xs uppercase tracking-wider mb-1">10,000 Workers =</p>
              <p className="text-3xl font-bold text-[hsl(14,70%,60%)]">$1.5-2M ARR</p>
            </div>
          </div>
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[hsl(14,70%,60%)]" />
            Expansion Path
          </h3>
          <p className="text-white/60 text-sm">
            <span className="text-white font-semibold">Minnesota</span> <ArrowRight className="w-3 h-3 inline mx-1" /> <span className="text-white font-semibold">Texas</span> <ArrowRight className="w-3 h-3 inline mx-1" /> Dense replication across metro areas
          </p>
        </div>
      </div>
    </SlideWrapper>
  );
}

function Slide12Team() {
  const founders = [
    {
      name: "Serge-Alain Wandji, Ph.D.",
      role: "President, CEO & Co-founder",
      credentials: [
        "VP Strategy, Business Development & Innovation",
        "Children's Hospitals of Minnesota",
      ],
      initials: "SW",
    },
    {
      name: "Jote Taddese, MSE",
      role: "CIO, COO & Co-founder",
      credentials: [
        "Chief Data, AI & Technology Executive",
        "Ex-President at Books For Africa — 64M+ Books Shipped",
      ],
      initials: "JT",
      link: "https://jotetaddese.com/",
    },
  ];

  const logos = [
    { name: "Cargill", src: "/images/logos/cargill.png", area: "Global Enterprise" },
    { name: "Children's Minnesota", src: "/images/logos/childrens-mn.png", area: "Pediatric Healthcare" },
    { name: "Ginkgo Bioworks", src: "/images/logos/ginkgo.png", area: "Biotech" },
    { name: "Medtronic", src: "/images/logos/medtronic.png", area: "Medical Technology" },
    { name: "OPTUM", src: "/images/logos/optum.png", area: "Health Services" },
    { name: "RALLY", src: "/images/logos/rally.png", area: "Health Engagement" },
  ];

  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(215,50%,12%)] to-[hsl(215,50%,18%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">Leadership</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 leading-tight">
        Built by People Who <span className="text-[hsl(14,70%,60%)]">Know the System</span>
      </h2>
      <p className="text-white/60 text-base sm:text-lg mb-6 sm:mb-8 max-w-3xl">
        Our founding team combines deep healthcare industry expertise with technology and community development experience.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-10">
        {founders.map((founder) => (
          <div key={founder.name} className="bg-white/5 border border-white/10 rounded-md p-4 sm:p-6" data-testid={`card-founder-${founder.initials}`}>
            <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[hsl(190,70%,30%)] border-2 border-[hsl(190,70%,45%)] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-base sm:text-lg">{founder.initials}</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white leading-snug">{founder.name}</h3>
                <p className="text-[hsl(14,70%,60%)] font-semibold text-sm mt-0.5">{founder.role}</p>
              </div>
            </div>
            <div className="space-y-1.5 pl-1">
              {founder.credentials.map((cred) => (
                <div key={cred} className="flex items-start gap-2.5">
                  <ChevronRight className="w-3.5 h-3.5 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />
                  <span className="text-white/70 text-sm leading-snug">{cred}</span>
                </div>
              ))}
            </div>
            {founder.link && (
              <a
                href={founder.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[hsl(190,70%,55%)] text-xs mt-4 hover:underline"
                data-testid={`link-founder-${founder.initials}`}
              >
                <Globe className="w-3 h-3" />
                {founder.link.replace("https://", "")}
              </a>
            )}
          </div>
        ))}
      </div>

      <div>
        <p className="text-white/40 text-xs uppercase tracking-widest mb-3 sm:mb-4 text-center">Experience From</p>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4">
          {logos.map((logo) => (
            <div
              key={logo.name}
              className="bg-white/[0.08] border border-white/10 rounded-md p-2 sm:p-3 flex flex-col items-center justify-center gap-1.5 sm:gap-2"
              data-testid={`logo-${logo.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
              <img
                src={logo.src}
                alt={`${logo.name} logo`}
                className="h-10 w-auto max-w-full object-contain brightness-0 invert opacity-90"
              />
              <span className="text-white/50 text-[10px] text-center leading-tight">{logo.name}</span>
            </div>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
}

function Slide13Raise() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(190,70%,25%)] via-[hsl(190,70%,32%)] to-[hsl(215,50%,20%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">The Raise</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 leading-tight">
        Raising <span className="text-[hsl(14,70%,60%)]">$5M</span> Seed
      </h2>
      <p className="text-white/50 mb-6 sm:mb-10 text-base sm:text-lg">18-24 months of runway. Density first. Scale second.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-10">
        <div className="bg-white/10 border border-white/15 rounded-md p-6 text-center">
          <Target className="w-8 h-8 text-[hsl(14,70%,60%)] mx-auto mb-3" />
          <p className="text-[hsl(14,70%,60%)] text-3xl font-bold mb-2">10,000</p>
          <h3 className="font-semibold mb-2">Workers Enrolled</h3>
          <p className="text-white/60 text-sm">Build density in the Minnesota beachhead market</p>
        </div>
        <div className="bg-white/10 border border-white/15 rounded-md p-6 text-center">
          <CircleDollarSign className="w-8 h-8 text-[hsl(14,70%,60%)] mx-auto mb-3" />
          <p className="text-[hsl(14,70%,60%)] text-3xl font-bold mb-2">Payroll</p>
          <h3 className="font-semibold mb-2">Integration</h3>
          <p className="text-white/60 text-sm">Seamless employer contribution flows from existing payroll systems</p>
        </div>
        <div className="bg-white/10 border border-white/15 rounded-md p-6 text-center">
          <MapPin className="w-8 h-8 text-[hsl(14,70%,60%)] mx-auto mb-3" />
          <p className="text-[hsl(14,70%,60%)] text-3xl font-bold mb-2">MN</p>
          <h3 className="font-semibold mb-2">Market Entry</h3>
          <p className="text-white/60 text-sm">Full deployment across the Twin Cities metro area</p>
        </div>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-md p-5 text-center">
        <p className="text-white/70 text-sm">
          <strong className="text-white">Strategy:</strong> Build density in one market <ArrowRight className="w-3 h-3 inline mx-1" /> prove unit economics <ArrowRight className="w-3 h-3 inline mx-1" /> replicate to Texas and beyond
        </p>
      </div>
    </SlideWrapper>
  );
}

function Slide14Vision() {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(215,50%,12%)] to-[hsl(215,50%,18%)] text-white">
      <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-6">The Vision</p>
      <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-6 sm:mb-10 leading-tight">
        The operating system for<br /><span className="text-[hsl(14,70%,60%)]">workforce healthcare</span> access
      </h2>
      <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-10">
        {[
          { phase: "Phase 1", title: "Liquidity + Purchasing Aggregation", desc: "Employer-funded wallets with volume-based provider negotiations. Fix the payment bottleneck.", status: "Now", color: "bg-[hsl(190,70%,30%)] border-[hsl(190,70%,40%)]" },
          { phase: "Phase 2", title: "On-Site Diagnostics + Payroll Embedding", desc: "Bring care closer to the workforce. Embed funding directly into employer payroll systems.", status: "Next", color: "bg-white/10 border-white/15" },
          { phase: "Phase 3", title: "Optional Expanded Protection Layer", desc: "Add an optional high-cost event protection layer once the infrastructure and density are established.", status: "Future", color: "bg-white/5 border-white/10" },
        ].map((phase) => (
          <div key={phase.phase} className={`${phase.color} border rounded-md p-4 sm:p-6 flex flex-col md:flex-row md:items-center gap-3 sm:gap-4`}>
            <div className="flex items-center gap-3 flex-shrink-0 md:w-48">
              <span className="text-[hsl(14,70%,60%)] font-bold text-sm uppercase">{phase.phase}</span>
              <span className="text-white/40 text-xs bg-white/10 rounded-full px-2 py-0.5">{phase.status}</span>
            </div>
            <div>
              <h3 className="font-semibold mb-1">{phase.title}</h3>
              <p className="text-white/60 text-sm">{phase.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </SlideWrapper>
  );
}

const FAQ_ITEMS = [
  {
    q: "Is the $100–200 PEPM price difference entirely from collective bargaining?",
    a: "Not entirely. Three factors combine: volume-based negotiated provider rates, a focused scope of covered conditions rather than comprehensive coverage, and removal of traditional insurance overhead — no claims adjudication, actuarial reserves, or broker commissions. HealthWallet is payment infrastructure, not insurance, so the entire cost structure is fundamentally different.",
  },
  {
    q: "Is the lower price tied to limited conditions, coverage ceilings, and limited providers?",
    a: "All three — by design. The 13 condition categories cover the highest-frequency healthcare needs for the working population: urgent care, dental, vision, prescriptions, preventive care, and mental health. Coverage ceilings keep the model sustainable. A curated provider network enables pre-negotiated rates. This isn't a limitation — it covers the 80% of healthcare needs workers face most often, at a price point employers can actually afford.",
  },
  {
    q: "Are SMEs willing to pay something nominal? What's the advantage over offering nothing?",
    a: "This is our core thesis. At $500–1,500/month traditional insurance is impossible for small employers. At $100–200 PEPM, the calculus changes: reduced turnover (replacing a worker costs 50–200% of their salary), improved productivity from healthier workers, a competitive hiring tool against larger employers, and potential tax advantages. It's not insurance vs. no insurance — it's something meaningful vs. nothing at all.",
  },
  {
    q: "Do providers need instant payments to participate? Does a limited network matter?",
    a: "Providers are motivated by guaranteed payment and patient volume — both of which HealthWallet delivers. Pre-funded wallets reduce collections overhead, and aggregated employer-group volume drives consistent patient flow. Instant payment accelerates adoption but isn't required. In the Minnesota pilot, a focused provider network is an advantage: it maintains quality, secures negotiated rates, and builds density. The network expands as volume grows.",
  },
  {
    q: "What differentiates this from a PEO like ADP or Insperity?",
    a: "PEOs are co-employment models bundling HR, payroll, compliance, and benefits administration. HealthWallet is specifically payment infrastructure for healthcare access — not HR services. Key differences: PEOs require significant operational control transfer; HealthWallet is an add-on. PEOs still offer traditional insurance at traditional prices; our cost structure is fundamentally different. PEOs target companies that can afford full-service outsourcing; we serve businesses too small or cost-constrained for PEOs.",
  },
  {
    q: "Does this go beyond chronic care?",
    a: "Yes. The condition categories include acute care (urgent care, minor emergencies), preventive care (screenings, wellness visits), dental, vision, prescriptions, and mental health — not just chronic disease management. The initial categories are designed around frequency of need across the working population, which skews heavily toward acute and preventive over chronic.",
  },
  {
    q: "Is there a sub-prime risk element given this population is currently uninsured?",
    a: "There is deferred-care risk, but the model mitigates it several ways: coverage ceilings cap per-person exposure; the pre-funded wallet means no underwriting risk — funds are collected before care is delivered; preventive care coverage catches issues earlier, reducing expensive downstream events; and the community fund model spreads risk across groups. HealthWallet doesn't bear insurance risk — it facilitates pre-funded access.",
  },
  {
    q: "Are the specific coverage areas a protective measure? What about broader coverage later?",
    a: "Exactly. Defining specific conditions creates cost-model predictability and avoids the open-ended liability that makes traditional insurance expensive. The tiered approach — focused at launch, expanding over time — lets us prove the model, build provider relationships, and accumulate utilization data before offering expanded coverage at higher price points. It's a deliberate land-and-expand strategy, not a limitation.",
  },
  {
    q: "How does this work for a covered employee who also has ACA coverage?",
    a: "HealthWallet is designed to complement ACA, not replace it. For employees with ACA marketplace plans (often high-deductible), HealthWallet helps cover out-of-pocket costs, copays, and services their ACA plan underserves — dental, vision, mental health. For employees without ACA coverage, HealthWallet provides a baseline of healthcare access. Because HealthWallet is structured as a pre-funded healthcare wallet — not an insurance product — it doesn't conflict with ACA requirements or trigger employer mandate issues for businesses under 50 employees, which is our primary target market.",
  },
];

function Slide15FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(215,50%,12%)] to-[hsl(215,50%,18%)] text-white" slideIndex={14}>
      <div data-testid="slide-faq">
        <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-3">Investor FAQ</p>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 leading-tight">
          Common Questions, <span className="text-[hsl(14,70%,60%)]">Direct Answers</span>
        </h2>
        <p className="text-white/50 text-sm mb-6">Click any question to expand the answer.</p>
        <div className="space-y-2">
          {FAQ_ITEMS.map((item, i) => (
            <div
              key={i}
              className="border border-white/10 rounded-md overflow-hidden"
              data-testid={`faq-item-${i}`}
            >
              <button
                className="w-full flex items-start gap-3 px-4 py-3 text-left hover-elevate"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                data-testid={`faq-question-${i}`}
              >
                <MessageCircle className="w-4 h-4 text-[hsl(14,70%,60%)] flex-shrink-0 mt-0.5" />
                <span className="text-[hsl(14,70%,70%)] font-medium text-sm leading-snug flex-1">{item.q}</span>
                <ChevronRight
                  className={`w-4 h-4 text-white/40 flex-shrink-0 mt-0.5 transition-transform duration-200 ${openIndex === i ? "rotate-90" : ""}`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-200 ${openIndex === i ? "max-h-96" : "max-h-0"}`}
              >
                <p className="text-white/70 text-sm leading-relaxed px-4 pb-4 pl-11">{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
}

function Slide16Closing({ onRequestDemo }: { onRequestDemo?: () => void }) {
  return (
    <SlideWrapper className="bg-gradient-to-br from-[hsl(190,70%,25%)] via-[hsl(190,70%,32%)] to-[hsl(215,50%,20%)] text-white">
      <div className="flex flex-col items-center text-center gap-8">
        <div className="flex flex-col items-center gap-4">
          <img 
            src={healthWalletLogo} 
            alt="HealthWallet Logo" 
            className="w-24 h-24 md:w-32 md:h-32 object-contain drop-shadow-[0_0_30px_rgba(255,255,255,0.15)]"
            width="1024"
            height="1024"
            data-testid="img-pitch-logo-closing"
            loading="lazy"
            decoding="async"
          />
          <span className="text-3xl font-bold tracking-wider text-white">HealthWallet</span>
        </div>
        <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold leading-tight max-w-4xl">
          Infrastructure for the workers<br />the system left behind.
        </h2>
        <p className="text-white/60 text-base sm:text-lg md:text-xl max-w-2xl">
          30 million workers have zero access to healthcare. Not because nobody cares — because no infrastructure exists. We're building it.
        </p>
        <div className="mt-6 sm:mt-8 bg-white/10 border border-white/20 rounded-md px-5 sm:px-8 py-3 sm:py-4">
          <p className="text-lg sm:text-xl md:text-2xl font-semibold">
            HealthWallet. <span className="text-[hsl(14,70%,60%)]">Not a plan. Infrastructure.</span>
          </p>
        </div>
        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={onRequestDemo}
            className="bg-[hsl(14,70%,55%)] border border-[hsl(14,70%,65%)] text-white"
            data-testid="button-request-demo"
          >
            <Send className="w-4 h-4 mr-2" />
            Request a Demo
          </Button>
          <Button
            variant="outline"
            className="border-white/30 text-white bg-white/5"
            onClick={() => window.open("/pilot-apply", "_blank")}
            data-testid="button-apply-pilot"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Apply for Pilot Program
          </Button>
        </div>
        <div className="mt-4 text-white/40 text-sm space-y-1">
          <p>Raising $5M Seed | Live demo available on request</p>
        </div>
        <p className="mt-4 text-white/20 text-[9px] sm:text-[10px] max-w-xl text-center leading-relaxed">
          HealthWallet is a technology platform that facilitates pre-funded healthcare access through employer-funded digital wallets and volume-based purchasing. HealthWallet does not provide, administer, or guarantee any health plan, insurance policy, or regulated financial product.
        </p>
      </div>
    </SlideWrapper>
  );
}

const demoFormSchema = insertDemoRequestSchema.extend({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Please enter a valid email"),
  company: z.string().min(1, "Company name is required"),
});
type DemoFormValues = z.infer<typeof demoFormSchema>;

function DemoRequestModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const form = useForm<DemoFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(demoFormSchema),
    defaultValues: { name: "", email: "", company: "", role: "", message: "" },
  });

  const mutation = useMutation({
    mutationFn: async (data: DemoFormValues) => {
      await apiRequest("POST", "/api/public/demo-request", data);
    },
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: () => {
      toast({ title: "Something went wrong", description: "Please try again later.", variant: "destructive" });
    },
  });

  const handleSubmit = form.handleSubmit((data) => mutation.mutate(data));

  const handleClose = () => {
    setSubmitted(false);
    form.reset();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="modal-demo-request">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-[hsl(215,50%,15%)] border border-white/15 rounded-md w-full max-w-md mx-4 p-6 sm:p-8 text-white">
        <Button size="icon" variant="ghost" onClick={handleClose} className="absolute top-4 right-4 text-white/50" data-testid="button-close-demo-modal">
          <X className="w-5 h-5" />
        </Button>

        {submitted ? (
          <div className="flex flex-col items-center text-center gap-4 py-6">
            <CheckCircle2 className="w-12 h-12 text-green-400" />
            <h3 className="text-xl font-bold">Demo Request Received</h3>
            <p className="text-white/60 text-sm">We'll be in touch shortly to schedule your personalized demo.</p>
            <Button onClick={handleClose} variant="outline" className="mt-2 border-white/30 text-white bg-white/5" data-testid="button-demo-done">
              Close
            </Button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-bold mb-1">Request a Demo</h3>
            <p className="text-white/50 text-sm mb-6">See how HealthWallet can serve your workforce.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="demo-name" className="text-white/70 text-sm">Full Name *</Label>
                <Input id="demo-name" {...form.register("name")} placeholder="John Smith" className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="input-demo-name" />
                {form.formState.errors.name && <p className="text-red-400 text-xs mt-1">{form.formState.errors.name.message}</p>}
              </div>
              <div>
                <Label htmlFor="demo-email" className="text-white/70 text-sm">Email *</Label>
                <Input id="demo-email" type="email" {...form.register("email")} placeholder="john@company.com" className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="input-demo-email" />
                {form.formState.errors.email && <p className="text-red-400 text-xs mt-1">{form.formState.errors.email.message}</p>}
              </div>
              <div>
                <Label htmlFor="demo-company" className="text-white/70 text-sm">Company *</Label>
                <Input id="demo-company" {...form.register("company")} placeholder="Acme Corp" className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="input-demo-company" />
                {form.formState.errors.company && <p className="text-red-400 text-xs mt-1">{form.formState.errors.company.message}</p>}
              </div>
              <div>
                <Label htmlFor="demo-role" className="text-white/70 text-sm">Your Role</Label>
                <Input id="demo-role" {...form.register("role")} placeholder="VP of Operations" className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="input-demo-role" />
              </div>
              <div>
                <Label htmlFor="demo-message" className="text-white/70 text-sm">Message</Label>
                <Textarea id="demo-message" {...form.register("message")} placeholder="Tell us about your interest..." rows={3} className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/30 resize-none" data-testid="input-demo-message" />
              </div>
              <Button type="submit" disabled={mutation.isPending} className="w-full bg-[hsl(14,70%,55%)] border border-[hsl(14,70%,65%)] text-white" data-testid="button-submit-demo">
                {mutation.isPending ? "Submitting..." : "Submit Request"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const SLIDES_BEFORE_CLOSING = [
  Slide1Title, Slide2Problem, Slide3Insight, Slide4Solution,
  Slide5BlueOcean, Slide6HowItWorks, Slide7AtomicNetwork, Slide8CommunityWallet,
  Slide9WhyMinnesota, Slide10Traction, Slide11Market, Slide12Team,
  Slide13Raise, Slide14Vision, Slide15FAQ,
];

const PITCH_SESSION_KEY = "hw_pitch_unlocked";
const PITCH_PASSWORD = "HealthWallet@)@^";

export default function PitchDeckPage() {
  usePageTitle("Investor Pitch Deck", "HealthWallet investor presentation — infrastructure for the non-insured workforce.");

  const [isUnlocked, setIsUnlocked] = useState(() => sessionStorage.getItem(PITCH_SESSION_KEY) === "1");
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState(false);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === PITCH_PASSWORD) {
      sessionStorage.setItem(PITCH_SESSION_KEY, "1");
      setIsUnlocked(true);
    } else {
      setPasswordError(true);
      setPasswordInput("");
    }
  };
  const [showDemoModal, setShowDemoModal] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goNext = useCallback(() => {
    setCurrentSlide((prev) => {
      if (prev >= TOTAL_SLIDES - 1) {
        setIsPlaying(false);
        return prev;
      }
      return prev + 1;
    });
  }, []);

  const goPrev = useCallback(() => {
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  }, []);

  const toggleSlideshow = useCallback(() => {
    setIsPlaying((prev) => {
      if (!prev && currentSlide >= TOTAL_SLIDES - 1) {
        setCurrentSlide(0);
        return true;
      }
      return !prev;
    });
  }, [currentSlide]);

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        goNext();
      }, SLIDESHOW_INTERVAL);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, goNext]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showDemoModal) return;
      if (e.key === "ArrowRight" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setIsPlaying(false);
        goNext();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        setIsPlaying(false);
        goPrev();
      } else if (e.key === "p" || e.key === "P") {
        e.preventDefault();
        toggleSlideshow();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrev, toggleSlideshow, showDemoModal]);

  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
        setIsPlaying(false);
        if (dx < 0) goNext();
        else goPrev();
      }
    };

    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [goNext, goPrev]);

  if (!isUnlocked) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[hsl(215,50%,12%)]">
        <div className="w-full max-w-sm mx-auto px-6">
          <div className="flex flex-col items-center gap-6">
            <img src={healthWalletLogo} alt="HealthWallet" className="w-16 h-16 object-contain" />
            <div className="text-center">
              <h1 className="text-white text-xl font-semibold tracking-tight">Investor Pitch Deck</h1>
              <p className="text-white/50 text-sm mt-1">Enter the access password to continue</p>
            </div>
            <form onSubmit={handlePasswordSubmit} className="w-full flex flex-col gap-3">
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
                placeholder="Password"
                autoFocus
                data-testid="input-pitch-password"
                className="w-full px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/30 text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
              />
              {passwordError && (
                <p className="text-[hsl(14,70%,60%)] text-xs text-center" data-testid="text-pitch-password-error">
                  Incorrect password. Please try again.
                </p>
              )}
              <Button
                type="submit"
                className="w-full bg-[hsl(14,70%,55%)] border border-[hsl(14,70%,65%)]/50 text-white font-medium"
                data-testid="button-pitch-unlock"
              >
                Access Deck
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const renderCurrentSlide = () => {
    if (currentSlide === TOTAL_SLIDES - 1) {
      return <Slide16Closing onRequestDemo={() => setShowDemoModal(true)} />;
    }
    const SlideComponent = SLIDES_BEFORE_CLOSING[currentSlide];
    return <SlideComponent />;
  };

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-[hsl(215,50%,12%)]" data-testid="pitch-deck">
      <div className="w-full h-full overflow-y-auto">
        {renderCurrentSlide()}
      </div>

      {isPlaying && (
        <div className="fixed top-0 left-0 right-0 h-1 z-50 bg-white/10">
          <div
            className="h-full bg-[hsl(14,70%,60%)]"
            style={{
              animation: `progress ${SLIDESHOW_INTERVAL}ms linear infinite`,
            }}
          />
          <style>{`@keyframes progress { from { width: 0%; } to { width: 100%; } }`}</style>
        </div>
      )}

      <Button
        size="icon"
        variant="ghost"
        onClick={() => { setIsPlaying(false); goPrev(); }}
        className={`fixed left-2 sm:left-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 border border-white/20 text-white transition-opacity ${currentSlide === 0 ? "opacity-0 pointer-events-none" : ""}`}
        data-testid="button-prev-slide"
        aria-label="Previous slide"
      >
        <ArrowLeft className="w-5 h-5" />
      </Button>

      <Button
        size="icon"
        variant="ghost"
        onClick={() => { setIsPlaying(false); goNext(); }}
        className={`fixed right-2 sm:right-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 border border-white/20 text-white transition-opacity ${currentSlide === TOTAL_SLIDES - 1 ? "opacity-0 pointer-events-none" : ""}`}
        data-testid="button-next-slide"
        aria-label="Next slide"
      >
        <ArrowRight className="w-5 h-5" />
      </Button>

      <SlideNumber
        current={currentSlide}
        total={TOTAL_SLIDES}
        onSlideSelect={(i) => { setIsPlaying(false); setCurrentSlide(i); }}
        slideNames={SLIDE_NAMES}
      />

      <Link
        href="/"
        data-testid="button-home"
        aria-label="Back to home"
        className="fixed top-4 left-4 z-50 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
      >
        <Home className="w-4 h-4" />
        <span className="hidden sm:inline">Home</span>
      </Link>

      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3">
        <Button
          size="icon"
          variant="ghost"
          onClick={toggleSlideshow}
          className="bg-white/10 border border-white/20 text-white"
          data-testid="button-slideshow-toggle"
          aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </Button>
        <span className="text-white/50 text-xs sm:text-sm font-medium" data-testid="text-slide-counter">
          {currentSlide + 1} / {TOTAL_SLIDES}
        </span>
      </div>

      <Button
        size="sm"
        onClick={() => setShowDemoModal(true)}
        className="fixed top-4 right-4 z-50 bg-[hsl(14,70%,55%)]/80 border border-[hsl(14,70%,65%)]/50 text-white text-xs font-medium"
        data-testid="button-request-demo-persistent"
      >
        <Send className="w-3 h-3 mr-1.5" />
        <span className="hidden sm:inline">Request Demo</span>
      </Button>

      <DemoRequestModal open={showDemoModal} onClose={() => setShowDemoModal(false)} />
    </div>
  );
}
